import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import icons from "../../assets/images/sprite-other-icons.svg";
import PaginationComponent from "../common/PaginationComponent";
import { freeGamesUsage, getFreeSpinsData } from "../bonusUsage/cl_BonusUsage";
import statickImg from "../../assets/images/popup/staticImg.webp";
import { getAllGames } from "../crashGames/cl_CrashSlice";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import { getFromLocalStorage } from "../../services/helper/cl_Helper";

const PAGE_SIZE = 10;
const formatDate = (isoDateString) => {
  if (!isoDateString) return "";
  const date = new Date(isoDateString);
  return date.toLocaleDateString("en-GB");
};
const calculateProgress = (spinsUsed, totalSpins) =>
  totalSpins > 0 ? Math.floor((spinsUsed / totalSpins) * 100) : 0;
const BonusProgressBar = React.memo(({ spinsUsed = 0, totalSpins = 0 }) => {
  const progressPercentage = calculateProgress(spinsUsed, totalSpins);
  return (
    <div className="SB-progressBar-container">
      <div className="SB-progressBar">
        <div
          className="SB-currentProgress"
          style={{ width: `${progressPercentage}%` }}
        />
        <span className="SB-currentProgressValue">{progressPercentage}%</span>
      </div>
      <div className="SB-progressBar-minmax">
        <span className="SB-progressBar-min">
          {spinsUsed} / {totalSpins} Spins
        </span>
      </div>
      <div className="SB-progressBar-legend">
        <div className="SB-progressBar-legendItem SB-used">
          <span className="SB-legendIndicator" />
          <span className="SB-legendlabel">Free Spins Used</span>
        </div>
        <div className="SB-progressBar-legendItem SB-available">
          <span className="SB-legendIndicator" />
          <span className="SB-legendlabel">Free Spins Available</span>
        </div>
      </div>
    </div>
  );
});
const SpinsInfo = React.memo(
  ({ spinsUsed = 0, totalSpins = 0, maxWinAmount = 0 }) => (
    <div
      className="SB-rollover-info"
      style={{ display: "flex", justifyContent: "space-around" }}
    >
      <div className="SB-rollover-item">
        <span className="SB-label">Spins Used: </span>
        <span className="SB-value">{spinsUsed}</span>
      </div>
      <div className="SB-rollover-item">
        <span className="SB-label">Total Spins: </span>
        <span className="SB-value">{totalSpins}</span>
      </div>
      <div className="SB-rollover-item">
        <span className="SB-label">Max Win: </span>
        <span className="SB-value">KES {maxWinAmount}</span>
      </div>
    </div>
  )
);
const GameCard = React.memo(
  ({ game, gameImage, onImageError, onGameClick }) => (
    <SplideSlide>
      <div className="SB-casinoGameItem">
        <div
          className="SB-casinoGameItem-container"
          onClick={() => onGameClick(game)}
        >
          <img
            src={gameImage}
            alt={game.gameName}
            width="100"
            height="100"
            onError={onImageError}
            style={{ cursor: "pointer" }}
          />
          <div className="SB-casinoGameItem-playGame-container">
            <div className="SB-btnPlayGame">
              <div className="SB-iconSVG">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="#FFF"
                >
                  <use xlinkHref={`${icons}#icon-casino-play`} />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="SB-casinoGameItem-footer">
          <div className="SB-casinoGameTitle">{game?.gameName || ""}</div>
          <div className="SB-casinoGameSubTitle">
            {game?.providerName === "split_the_pot"
              ? "Stp"
              : game?.providerName === "turbogames"
              ? "Turbo"
              : game?.providerName}
          </div>
        </div>
      </div>
    </SplideSlide>
  )
);
const BonusDetailsLoader = React.memo(() => (
  <div className="SB-bonus-moreDetails">
    <div className="SB-section-loader">
      <div className="SB-section-loader-container">
        {Array.from({ length: 4 }, (_, i) => (
          <span key={i} className="SB-dot" />
        ))}
      </div>
    </div>
  </div>
));

const BonusDetailsContent = React.memo(
  ({ data, isLoading, userBonusId, spinsDataCache }) => {
    const { getAllgamesList } = useSelector((state) => state?.crash);
    const navigate = useNavigate();
    const currentSpinsData = useMemo(() => {
      return spinsDataCache?.get(userBonusId) || [];
    }, [spinsDataCache, userBonusId]);
    const combinedGames = useMemo(() => {
      return (
        getAllgamesList?.reduce((acc, category) => {
          return acc.concat(category?.game || []);
        }, []) || []
      );
    }, [getAllgamesList]);
    const getGameImage = useCallback(
      (game) => {
        const matchedGame = combinedGames.find(
          (cGame) =>
            cGame.game_code === game.gameCode &&
            cGame.provider?.toLowerCase() === game.providerName?.toLowerCase()
        );
        if (matchedGame?.image) {
          return matchedGame.image.configurl &&
            matchedGame.image.configurl !== "NoIcon"
            ? matchedGame.image.configurl
            : matchedGame.image.url && matchedGame.image.url !== "NoIcon"
            ? matchedGame.image.url
            : statickImg;
        }
        return `images/casino/${game.gameCode}-thumb.webp`;
      },
      [combinedGames]
    );
    const handleImageError = useCallback((e) => {
      e.target.src = statickImg;
    }, []);

    const navigateToGame = useCallback(
      (spinsGame) => {
        const token = getFromLocalStorage("gk");
        const matchedGame = combinedGames.find(
          (cGame) =>
            cGame.game_code === spinsGame.gameCode &&
            cGame.provider?.toLowerCase() ===
              spinsGame.subProviderName?.toLowerCase()
        );
        if (token) {
          if (matchedGame) {
            const stateData = encodeURIComponent(
              JSON.stringify({ gameData: matchedGame, gameClose: true })
            );
            window.location.href = `/gamelaunch?state=${stateData}`;
          } else {
            const gameObject = {
              game_code: spinsGame.gameCode,
              provider: spinsGame.subProviderName,
              name: spinsGame.gameName,
              image: {
                url: getGameImage(spinsGame),
                configurl: getGameImage(spinsGame),
              },
            };
            const stateData = encodeURIComponent(
              JSON.stringify({ gameData: gameObject, gameClose: true })
            );
            window.location.href = `/gamelaunch?state=${stateData}`;
          }
        } else {
          const gameToStore = matchedGame || {
            game_code: spinsGame.gameCode,
            provider: spinsGame.subProviderName,
            name: spinsGame.gameName,
            image: {
              url: getGameImage(spinsGame),
              configurl: getGameImage(spinsGame),
            },
          };
          localStorage.setItem("pendingGame", JSON.stringify(gameToStore));
          navigate("/signin");
        }
      },
      [combinedGames, getGameImage, navigate]
    );

    if (!data?.isFreeSpinBonus) return null;
    if (isLoading) {
      return <BonusDetailsLoader />;
    }
    if (!currentSpinsData.length) {
      return (
        <div className="SB-bonus-moreDetails">
          <div className="SB-msgBox">
            <div className="SB-msgBox-container">
              No free spins data available for this bonus.
            </div>
          </div>
        </div>
      );
    }

    const totalSpinsUsed = currentSpinsData.reduce(
      (sum, game) => sum + (game.freeSpinsUsed || 0),
      0
    );
    const totalSpinsAllocated = currentSpinsData.reduce(
      (sum, game) => sum + (game.freeSpinsAllocated || 0),
      0
    );
    const totalMaxWinAmount = currentSpinsData.reduce(
      (sum, game) => sum + (game.freeSpinMaxWinAmount || 0),
      0
    );

    return (
      <div className="SB-bonus-moreDetails">
        <div className="SB-bonusIDType-info">
          <div className="SB-bonusInfo-item SB-bonusInfo-bonusID">
            <div className="SB-lable">Bonus ID:</div>
            <div className="SB-value">{data?.userBonusId}</div>
          </div>
          <div className="SB-bonusInfo-item SB-bonusInfo-bonusType">
            <div className="SB-lable">Bonus Type:</div>
            <div className="SB-value">{data?.bonusType}</div>
          </div>
        </div>

        <div className="SB-bonusProgress-info">
          <BonusProgressBar
            spinsUsed={totalSpinsUsed}
            totalSpins={totalSpinsAllocated}
          />
        </div>

        <div className="SB-bonusInfo-item SB-bonusApplicable">
          <div className="SB-bonusApplicableList">
            <div className="splide SB-splide-NEGames SB-NE-GamesList splide--slide splide--ltr splide--draggable is-active is-initialized">
              <div
                className="splide__track"
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <Splide
                  hasTrack={false}
                  options={{
                    type: "slide",
                    pagination: false,
                    autoplay: true,
                    autoWidth: true,
                    arrows: false,
                    lazyLoad: "sequential",
                  }}
                >
                  <SplideTrack>
                    {currentSpinsData.map((game, index) => (
                      <GameCard
                        key={`${game.gameCode}-${game.id}-${index}`}
                        game={game}
                        gameImage={getGameImage(game)}
                        onImageError={handleImageError}
                        onGameClick={navigateToGame}
                      />
                    ))}
                  </SplideTrack>
                </Splide>
              </div>
            </div>
          </div>
        </div>
        <SpinsInfo
          spinsUsed={totalSpinsUsed}
          totalSpins={totalSpinsAllocated}
          maxWinAmount={totalMaxWinAmount}
        />
        <div className="SB-bonusInfo-section">
          <div className="SB-bonusInfo-section__title">
            Free Spins Usage Criteria
          </div>
          <div className="SB-bonusTerms-info">
            <ul>
              <li>
                <div className="SB-label">No. Free spins:</div>
                <div className="SB-value">{totalSpinsAllocated}</div>
              </li>
              <li>
                <div className="SB-label">Free Spin Amount:</div>
                <div className="SB-value">
                  KES {currentSpinsData[0]?.freeSpinAmount || 0}
                </div>
              </li>
              <li>
                <div className="SB-label">Max Win Amount:</div>
                <div className="SB-value">KES {totalMaxWinAmount}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
);

const LoaderComponent = React.memo(() => (
  <div className="SB-section-loader">
    <div className="SB-section-loader-container">
      {Array.from({ length: 4 }, (_, i) => (
        <span key={i} className="SB-dot" />
      ))}
    </div>
  </div>
));

const NoDataMessage = React.memo(() => (
  <div className="SB-msgBox">
    <div className="SB-msgBox-container">
      Currently there is no data available
    </div>
  </div>
));

const AccordionArrow = React.memo(({ isExpanded, onClick }) => (
  <div
    className="SB-iconSVG SB-accordionArrow SB-expanded"
    title="Arrow Right"
    onClick={onClick}
  >
    <svg xmlns="http://www.w3.org/2000/svg">
      <use
        xlinkHref={`${icons}#icon-angle-arrow-${isExpanded ? "down" : "up"}`}
      />
    </svg>
  </div>
));

const BonusTableRow = React.memo(
  ({ data, index, isExpanded, onToggle, isLoadingDetails, spinsDataCache }) => (
    <tbody>
      <tr>
        <td>{formatDate(data?.addedDate)}</td>
        <td>Free Games</td>
        <td>{formatDate(data?.bonusExpiry)}</td>
        <td className="SB-bonus-status">
          <span className="SB-status SB-active">{data?.status}</span>
        </td>
        <td onClick={onToggle}>
          <AccordionArrow isExpanded={isExpanded} />
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan="7" className="SB-td-bonus-moreDetails">
            <BonusDetailsContent
              data={data}
              isLoading={isLoadingDetails}
              userBonusId={data.userBonusId}
              spinsDataCache={spinsDataCache}
            />
          </td>
        </tr>
      )}
    </tbody>
  )
);

const BonusListItem = React.memo(
  ({ data, index, isExpanded, onToggle, isLoadingDetails, spinsDataCache }) => (
    <div className="SB-bonus-item">
      <div className="SB-bonus-item-content">
        <div className="SB-bonus__col SB-col1">
          <div className="SB-bonus-dateID">
            <div className="SB-bonusID">DATE</div>
            <div className="SB-bonusDate">{formatDate(data?.addedDate)}</div>
          </div>
        </div>

        <div className="SB-bonus__col SB-col2">
          <div className="SB-bonusTypeApplicable">
            <div className="SB-bonusType">Free Games</div>
            <div className="SB-bonusApplicableTo">
              Applicable to: {data?.applicableType}
            </div>
          </div>
          <div className="SB-bonus-statusAmount">
            <div className="SB-bonusAmount">KES {data?.totalBonus}</div>
            <div className="SB-statusAccordionArrow">
              <span className="SB-status SB-active">{data?.status}</span>
              <AccordionArrow isExpanded={isExpanded} onClick={onToggle} />
            </div>
          </div>
        </div>
        <div className="SB-bonus__col SB-col3">
          <div className="SB-bonusExpiry">
            <span>Expiry:</span> <span>{formatDate(data?.bonusExpiry)}</span>
          </div>
        </div>
      </div>
      {isExpanded && (
        <BonusDetailsContent
          data={data}
          isLoading={isLoadingDetails}
          userBonusId={data.userBonusId}
          spinsDataCache={spinsDataCache}
        />
      )}
    </div>
  )
);

const FreeGames = (props) => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state?.signIn);
  const { freeGamesData, loader } = useSelector((state) => state?.bonusUsage);
  const [expandedBonusIds, setExpandedBonusIds] = useState(new Set());
  const [loadingBonusDetails, setLoadingBonusDetails] = useState(new Set());
  const [spinsDataCache, setSpinsDataCache] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = useMemo(() => {
    return (
      freeGamesData?.data?.filter((item) => item.isFreeSpinBonus === true) || []
    );
  }, [freeGamesData?.data]);

  const hasData = !loader && filteredData.length > 0;
  const noData = !loader && filteredData.length === 0;

  const toggleBonusDetails = useCallback(
    async (index) => {
      const bonusData = filteredData[index];
      const userBonusId = bonusData?.userBonusId;
      if (!userBonusId) return;

      setExpandedBonusIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(userBonusId)) {
          newSet.delete(userBonusId);
          setLoadingBonusDetails((loadingPrev) => {
            const loadingNewSet = new Set(loadingPrev);
            loadingNewSet.delete(userBonusId);
            return loadingNewSet;
          });
        } else {
          newSet.add(userBonusId);
          if (userDetails?.userId) {
            if (!spinsDataCache.has(userBonusId)) {
              setLoadingBonusDetails((loadingPrev) => {
                const loadingNewSet = new Set(loadingPrev);
                loadingNewSet.add(userBonusId);
                return loadingNewSet;
              });
              dispatch(getFreeSpinsData(userBonusId, userDetails.userId))
                .then((response) => {
                  const spinsData = response?.data?.result?.data?.data || [];
                  setSpinsDataCache(
                    (prev) => new Map(prev.set(userBonusId, spinsData))
                  );
                })
                .catch((error) => {
                  console.error("Error fetching free spins data:", error);
                  setSpinsDataCache(
                    (prev) => new Map(prev.set(userBonusId, []))
                  );
                })
                .finally(() => {
                  setLoadingBonusDetails((loadingPrev) => {
                    const loadingNewSet = new Set(loadingPrev);
                    loadingNewSet.delete(userBonusId);
                    return loadingNewSet;
                  });
                });
            }
          }
        }
        return newSet;
      });
    },
    [filteredData, userDetails?.userId, dispatch, spinsDataCache]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    dispatch(getAllGames());
  }, [dispatch]);

  useEffect(() => {
    setExpandedBonusIds(new Set());
    setLoadingBonusDetails(new Set());
    setSpinsDataCache(new Map());
  }, [props]);

  useEffect(() => {
    if (userDetails?.userId) {
      dispatch(freeGamesUsage(userDetails.userId, currentPage - 1));
    }
  }, [userDetails?.userId, currentPage, dispatch]);

  if (loader && filteredData.length === 0) return <LoaderComponent />;
  if (noData) return <NoDataMessage />;

  return (
    <>
      <div className="SB-bonusUsage-table">
        <div className="SB-table">
          <table>
            {hasData && (
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Bonus Name</th>
                  <th>Date of Expiry</th>
                  <th className="SB-bonus-status">Status</th>
                  <th className="SB-th-more">More</th>
                </tr>
              </thead>
            )}
            {filteredData.map((data, index) => (
              <BonusTableRow
                key={`table-${data.userBonusId || index}`}
                data={data}
                index={index}
                isExpanded={expandedBonusIds.has(data.userBonusId)}
                isLoadingDetails={loadingBonusDetails.has(data.userBonusId)}
                onToggle={() => toggleBonusDetails(index)}
                spinsDataCache={spinsDataCache}
              />
            ))}
          </table>
        </div>
      </div>

      <div className="SB-bonusList">
        {filteredData.map((data, index) => (
          <BonusListItem
            key={`list-${data.userBonusId || index}`}
            data={data}
            index={index}
            isExpanded={expandedBonusIds.has(data.userBonusId)}
            isLoadingDetails={loadingBonusDetails.has(data.userBonusId)}
            onToggle={() => toggleBonusDetails(index)}
            spinsDataCache={spinsDataCache}
          />
        ))}
      </div>
      {hasData && (
        <PaginationComponent
          currentPage={currentPage}
          totalCount={filteredData.length}
          pageSize={PAGE_SIZE}
          onPageChange={handlePageChange}
          setCurrentPage={setCurrentPage}
        />
      )}
    </>
  );
};

export default React.memo(FreeGames);
